// SharedAnswers component for collaborative review and answer application.
// There are two types of question entities:
// 1. Factor questions (question.id) — belong to the main calculator (tt-calculator), used for calculation steps and factor selection.
// 2. Shared questions (share.question.outer_id) — come from shared answers, reflect the structure of shared data and are linked to shared responses.
// When a user applies an answer from sharing, it replaces their existing answer for that question.
// This file manages the mapping between these entities, displays shared answers, and handles answer application logic.
import React, { use, useContext, useEffect, useMemo, useRef, useState } from 'react';
// Importing necessary components and utilities
import { Button, Select, Radio } from 'tt-ui-lib/core';
import clsx from 'clsx';
import styles from './SharedAnswers.module.scss';
import { ArrowAscIcon, SearchIcon } from 'tt-ui-kit';
import { CalculatorsContext, DraftContext } from '../../context';
import { CALC_MODELS, STATES_MAP } from '../../constants';
import useDebounce from '../../utils/useDebounce';
import RecursiveAccordion from '../../components/recursiveAccordion/RecursiveAccordion';
import { useNavigate } from 'react-router-dom';
import UserBlockSimple from '../../components/userBlockSimple/UserBlockSimple';
import answersResolver from '../../components/answers/answersResolver';
import Dot3Menu from '../../components/dot3menu/Dot3Menu';
import Highlighter from 'react-highlight-words';
import { carbonStrategy } from './strategy/carbonStrategy';
import { STRATEGIES } from './strategy/questionStrategies';
import { StatusIcon } from './components/StatusIcon';
import { answerStateSet, approveAnswersBatch } from '../../api/rest/list';
import HelpersFiles from '../../components/helpersFiles/HelpersFiles';
import { reduce } from 'd3';
import { calc } from 'antd/es/theme/internal';

// SharedAnswers component.
// Displays shared questions and answers for collaborative review.
// Uses CalculatorsContext for questions/answers/steps/config and DraftContext for draft info.
// Supports searching, filtering, and applying shared answers.
// Renders question tree and user answers, with navigation and batch actions.

// Constants for menu options and strategies
const MENU_LIST = ['Cancel access', 'Delete answer'];

const SharedAnswers = (props) => {
  const { baseRoute } = props;
  const navigate = useNavigate();
  // Accessing context values
  const {
    sharedQuestionAnswers,
    calcSteps,
    questions: contextQuestions,
    answers: contextAnswers,
    setAnswersData,
    setDirtyAnswersMap,
  } = useContext(CalculatorsContext);
  const { draftId } = useContext(DraftContext);

  // State variables for managing UI and data
  const [searchValue, setSearchValue] = useState('');
  const [inputValue, setInputValue] = useState('');
  const [searchUser, setSearchUser] = useState([]);
  const [users, setUsers] = useState([]);
  const [sharedQuestionsCollection, setSharedQuestionsCollection] = useState(null);
  const [sharedQuestionsTree, setSharedQuestionsTree] = useState(null);
  const [sharedAnswersCollection, setSharedAnswersCollection] = useState(null);
  const [selectedQuestionId, setSelectedQuestionId] = useState(null);
  const [selectedAnswerIds, setSelectedAnswerIds] = useState({});
  const [filteredQuestionIds, setFilteredQuestionIds] = useState(null);
  const [filteredAnswerIds, setFilteredAnswerIds] = useState(null);

  const [questionsSearchStrings, setQuestionsSearchStrings] = useState([]);
  const [expandedIds, setExpandedIds] = useState([]);

  const [questionStatuses, setQuestionStatuses] = useState(null);

  // Toggles the accordion for a specific question
  const toggleAccordion = (id) => {
    setExpandedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  // Collects the path of "zero children" nodes in the tree
  const getFirstPathIds = (node) => {
    const path = [];
    let current = node;
    while (current) {
      if (current.code) path.push(current.code);
      if (current.sub && current.sub.length > 0) {
        [current] = current.sub;
      } else {
        break;
      }
    }
    return path;
  };

  // Expands the first path in the tree on initial load
  useEffect(() => {
    if (sharedQuestionsTree?.length > 0) {
      const firstPath = getFirstPathIds(sharedQuestionsTree[0]);
      setExpandedIds(firstPath);
    }
  }, [sharedQuestionsTree]);

  // Debounced search input handler
  const debounce = useDebounce((data) => {
    setSearchValue(data);
  }, 500);

  const memoizedQuestionData = useMemo(
    () =>
      Object.keys(STRATEGIES).reduce(
        (acc, strategy) => ({
          ...acc,
          ...STRATEGIES[strategy].getQuestionData(sharedQuestionAnswers, contextQuestions),
        }),
        {}
      ),
    [contextQuestions, sharedQuestionAnswers]
  );

  const getQuestionData = (question) => memoizedQuestionData[question.compositeId] || {};

  // Processes shared questions and answers into collections and trees
  useEffect(() => {
    if (!calcSteps?.length > 0 || !sharedQuestionAnswers) return;
    // Each share is processed to extract users information for selection filter
    const newUsers = sharedQuestionAnswers.reduce((acc, share) => {
      const { addresseeFirstName, addresseeLastName } = share;
      const { userId } = share.share;
      // Check if the user already exists in the collection
      const existingUser = acc.find((el) => el.id === userId);
      if (!existingUser) {
        acc.push({
          id: userId,
          firstName: addresseeFirstName,
          lastName: addresseeLastName,
          answersIds: [],
        });
      } else {
        // If the user already exists, just update the answersIds
        existingUser.answersIds = [...existingUser.answersIds, ...share.answers.map((el) => el.id)];
      }
      return acc;
    }, []);

    // Each share can have multiple questions and answers, so we need to flatten them
    const uniqueQuestions = sharedQuestionAnswers.reduce(
      (acc, share) => {
        const { q, a } = acc;
        const {
          questions: rawQuestions,
          answers: sharedAnswers,
          addresseeFirstName,
          addresseeLastName,
        } = share;
        const { userId } = share.share;

        // We need to add the compositeId to each answer
        // This is necessary for the strategy to work correctly
        const sharedQuestions = rawQuestions.map((question) => ({
          ...question,
          compositeId: STRATEGIES[question.type]?.getCompositeId(question) ?? question.id,
        }));

        // create a map of questions to answers based on compositeId
        const newAnswers = {
          ...sharedAnswers.reduce((acc1, answer) => {
            const { compositeId } = sharedQuestions.find((el) => el.id === answer.questionId);
            // add answerId to the users collection for filtering by user
            const user = newUsers.find((el) => el.id === userId);
            const { answersIds } = user;
            answersIds.push(answer.id);
            return {
              ...acc1,
              [compositeId]: [
                ...(acc1[compositeId] ?? []),
                {
                  ...answer,
                  user,
                  compositeId,
                },
              ],
            };
          }, a),
        };

        const newQuestions = sharedQuestions.reduce((acc1, question) => {
          const questionStep =
            question.type === CALC_MODELS.CUSTOM_CO2
              ? calcSteps.find((el) => el.id === `${CALC_MODELS.CUSTOM_CO2}_${question.outerId}`)
              : calcSteps.find((el) => el.id === question.outerId);
          return [
            ...(acc1.filter((el) => el.id !== question.id) ?? []),
            {
              ...question,
              route: questionStep.route,
              data: { ...question.data, ...getQuestionData(question) },
            },
          ];
        }, q);
        return { q: [...newQuestions], a: { ...newAnswers } };
      },
      { q: [], a: {} }
    );

    const { q: newSharedQuestions, a: newSharedAnswers } = uniqueQuestions;

    const questionTree = newSharedQuestions.reduce(
      (acc, question) =>
        STRATEGIES[question.type].getStructure(acc, question, memoizedQuestionData),
      []
    );

    setSharedAnswersCollection(newSharedAnswers);
    setSharedQuestionsCollection(newSharedQuestions);
    setSharedQuestionsTree(questionTree);
    setUsers(newUsers);
  }, [calcSteps, sharedQuestionAnswers]);

  const buildAnswerState = (question) => {
    const { type } = question;
    if (!contextAnswers[type]) return STATES_MAP.UNANSWERED;
    const answerExists = STRATEGIES[type].isAnswerExists(question, contextAnswers[type]);
    return answerExists ? STATES_MAP.ANSWERED : STATES_MAP.UNANSWERED;
  };

  const buildShareState = (question) => {
    const { compositeId } = question;
    if (!sharedAnswersCollection[compositeId]?.length) return STATES_MAP.EPMTY;
    const newAnswers = sharedAnswersCollection[compositeId].some((el) => el.state === 'NEW');
    return newAnswers ? STATES_MAP.NEW : STATES_MAP.VIEWED;
  };

  const buildOverrideState = (question, questionState) => {
    const { compositeId } = question;
    return questionState === STATES_MAP.ANSWERED && selectedAnswerIds?.[compositeId];
  };

  const getStatus = (question) => {
    const questionState = buildAnswerState(question);
    const shareState = buildShareState(question);
    const overrided = buildOverrideState(question, questionState);
    const status = `${questionState}_${overrided ? STATES_MAP.OVERRIDEN : shareState}`;
    return status;
  };

  const setNewStates = (ids, state) => {
    const newAnswers = Object.keys(sharedAnswersCollection).reduce((acc, key) => {
      const answers = sharedAnswersCollection[key].map((el) => {
        if (ids.includes(el.id)) {
          return { ...el, state };
        }
        return el;
      });
      return { ...acc, [key]: answers };
    }, {});
    setSharedAnswersCollection(newAnswers);
  };

  const updateStatus = (id) => {
    const viewedAnswers = sharedAnswersCollection[id]?.filter((el) => el.state === 'NEW');
    if (!viewedAnswers?.length) return;

    const ids = viewedAnswers.map((el) => el.id);

    answerStateSet({ ids, state: 'VIEWED', calculator_id: draftId })
      .then((res) => {
        const { data, success } = res.data;
        if (success) {
          setNewStates(ids, 'VIEWED');
        }
      })
      .catch((err) => {
        console.log('err', err);
      })
      .finally(() => {
        console.log('finally', 'finally');
      });
  };

  const debounceTimerRef = useRef();

  useEffect(() => {
    if (!selectedQuestionId) return;

    // clear timer if it exists
    clearTimeout(debounceTimerRef.current);

    // start new timer
    debounceTimerRef.current = setTimeout(() => {
      // update status of the selected question
      updateStatus(selectedQuestionId);
    }, 5000);

    // cleanup function to clear the timer when component unmounts or selectedQuestionId changes
    return () => clearTimeout(debounceTimerRef.current);
  }, [selectedQuestionId]);

  // Updates question statuses when shared questions or answers change
  useEffect(() => {
    if (!sharedQuestionsCollection || !contextAnswers || !sharedAnswersCollection) return;
    const statuses = sharedQuestionsCollection.reduce(
      (acc, q) => ({
        ...acc,
        [q.id]: getStatus(q),
      }),
      {}
    );
    setQuestionStatuses(statuses);
  }, [sharedQuestionsCollection, contextAnswers, selectedAnswerIds, sharedAnswersCollection]);

  const collectAnswers = () => {
    const approvals = Object.keys(selectedAnswerIds).reduce((acc, key) => {
      const newAnswers = sharedAnswersCollection[key]?.filter(
        (el) => el.id === selectedAnswerIds[key]
      );
      if (newAnswers?.length > 0) {
        newAnswers.forEach((el) => {
          acc.push({
            question_id:
              el.type === CALC_MODELS.CUSTOM_CO2
                ? el.questionId
                : sharedQuestionsCollection.find((q) => q.id === el.questionId)?.outerId,
            answer_id: el.id,
            model: el.type,
          });
        });
      }
      return acc;
    }, []);

    return {
      calculator_id: draftId,
      approvals,
    };
  };

  // Applies the selected answer to the context
  const onApplyAnswer = async () => {
    const newAnswers = sharedQuestionsCollection.reduce(
      (acc, question) =>
        selectedAnswerIds[question.compositeId]
          ? {
              ...acc,
              [question.type]: STRATEGIES[question.type].getAnswersStructure(
                question,
                acc[question.type],
                sharedAnswersCollection,
                selectedAnswerIds
              ),
            }
          : { ...acc },
      contextAnswers
    );

    const approvedAnswers = collectAnswers();
    try {
      const res = await approveAnswersBatch(approvedAnswers);
      const { data, success } = res.data;
      if (!success) {
        console.error('File approval failed: ', res);
        throw new Error('File approval failed'); // Stop execution if approval fails
      } else {
        const newSharedAnswers = Object.keys(sharedAnswersCollection).reduce((acc, key) => {
          const answers = sharedAnswersCollection[key].map((el) => {
            const newState = data.find((ans) => ans.id === el.id);
            if (newState) {
              return { ...el, state: newState.state }; // Update the state of the approved answer
            }
            return el;
          });
          return { ...acc, [key]: answers };
        }, {});
        setSharedAnswersCollection(newSharedAnswers);
        // Update the state of the approved answer in the shared answers collection
        // If the answer is approved, update its state to 'APPLIED'
      }
    } catch (e) {
      console.error('Failed to approve files:', e);
      return;
    }

    // Set the new answers to the context
    setAnswersData({ ...newAnswers });
    const modelsToSave = [
      ...Object.keys(selectedAnswerIds).reduce(
        (acc, key) => acc.add(sharedQuestionsCollection.find((el) => el.compositeId === key)?.type),
        new Set()
      ),
    ];
    console.log('modelsToSave', modelsToSave);

    // Set the models for saving
    setDirtyAnswersMap({
      ...modelsToSave.reduce((acc, modelName) => {
        acc[modelName] = true;
        return acc;
      }, {}),
    });
  };

  // Updates search strings for questions
  useEffect(() => {
    if (!sharedQuestionsCollection) return;
    setQuestionsSearchStrings(
      sharedQuestionsCollection.map((q) => ({ ...STRATEGIES[q.type].getQuestionsSearchStrings(q) }))
    );
  }, [sharedQuestionsCollection]);

  // Filters questions and answers based on search input and user selection
  const { filteredQuestionsIds } = useMemo(() => {
    if (!sharedAnswersCollection) return { exceptAnswers: [], exceptQuestions: [] };

    const { value: userId } = searchUser;

    // Filter answers based on user selection
    const newFilteredAnswersIds = Object.keys(sharedAnswersCollection ?? {}).reduce((acc, key) => {
      const answers = sharedAnswersCollection[key];
      const newAnswers = (answers ?? [])
        .filter((el) => userId == null || el.user.id === userId)
        .map((el) => el.id);
      if (newAnswers.length > 0) {
        acc.push({ compositeId: key, answers: newAnswers });
      }
      return acc;
    }, []);

    // Filter questions based on search input and user selection
    const newFilteredQuestionsIds = questionsSearchStrings
      .filter(
        (el) => !userId || newFilteredAnswersIds.some((ans) => ans.compositeId === el.compositeId)
      )
      .filter((el) =>
        el.strings.some((str) => str && str.toLowerCase().includes(inputValue.toLowerCase()))
      )
      .map((el) => el.compositeId);
    return {
      filteredQuestionsIds: newFilteredQuestionsIds,
    };
  }, [inputValue, searchUser, sharedAnswersCollection, questionsSearchStrings]);

  // Filters the question tree based on filtered IDs
  const filterQuestionTree = (tree, filteredIds) =>
    tree
      ?.map((node) => {
        const newNode = { ...node };

        if (newNode.sub) {
          newNode.sub = filterQuestionTree(newNode.sub, filteredIds);
        }

        if (newNode.children) {
          newNode.children = newNode.children.filter((child) =>
            filteredIds.includes(child.compositeId)
          );
        }

        const hasSub = newNode.sub && newNode.sub.length > 0;
        const hasChildren = newNode.children && newNode.children.length > 0;

        if (hasSub || hasChildren) {
          return newNode;
        }

        return null;
      })
      .filter(Boolean);

  const filteredTree = filterQuestionTree(sharedQuestionsTree, filteredQuestionsIds);

  // Checks if any answer is selected
  const isAnswerChecked = Object.keys(selectedAnswerIds).some((key) => !!selectedAnswerIds[key]);

  // Handles search input changes
  const onSearchChange = (e) => {
    setInputValue(e.target.value);
    debounce(e.target.value);
  };

  // Navigates to a specific question
  const onGoToQuestionClick = (e, route) => {
    e.stopPropagation();
    navigate(route);
  };

  // Selects or deselects a question
  const onQuestionClick = (e, qId) => {
    e.stopPropagation();
    setSelectedQuestionId((value) => (value === qId ? null : qId));
  };

  // Handles menu actions
  const onMenuClick = (option) => {
    if (option === 'Download');
  };

  const onBackClick = () => {
    if (window.history.length > 2) {
      // If there is a history, go back to the previous page
      navigate(-1);
    } else {
      // If no history, navigate to the root
      navigate(baseRoute, { replace: true });
    }
  };

  // Debugging hooks (commented out)
  // useEffect(() => { console.log('', ) }, [dependency]);

  return (
    <>
      <div className={styles.answersWrapper}>
        <div className={styles.answersInfoBlock}>
          <div className={styles.answersInfo}>
            <p>
              Your colleagues have provided answers to your question! Review the answers and select
              the one that best addresses your query. Once you&apos;ve made your choice, click the
              &apos;ApplyAnswer&apos; button. The selected answer will be automatically inserted
              into the calculator, where you can still edit it if necessary before finalizing it.
              This feature helps you leverage your colleagues&apos; expertise and complete your
              calculations more efficiently!
            </p>
          </div>
          <div className={styles.answersfiltre}>
            <Select
              label="Select user's answers"
              name="user"
              value={searchUser}
              options={users.map((el) => ({
                value: el.id,
                label: `${el.firstName} ${el.lastName}`,
              }))}
              className={styles.componentInput}
              onChange={(_, option) => setSearchUser({ ...option })}
              allowClear
            />
            <div className={styles.searchBox}>
              <div className={styles.searchImg}>
                <SearchIcon style={{ width: 18, height: 18, opacity: 0.8 }} />
              </div>
              <div className={styles.search}>
                <input
                  placeholder="Search..."
                  className={styles.searchInput}
                  value={inputValue}
                  onChange={onSearchChange}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Main content section */}
        <div className={styles.mainActionBlock}>
          {/* Left panel with questions */}
          <div className={styles.questionActionBlock}>
            <div className={styles.scopeList}>
              {filteredTree?.length > 0 &&
                filteredTree.map((node) => (
                  <RecursiveAccordion
                    key={node.code}
                    node={node}
                    expandedIds={expandedIds}
                    onToggle={toggleAccordion}
                    renderElement={(el) => (
                      <ul className={styles.questionList}>
                        {el.map((q) => (
                          <div
                            className={clsx(
                              styles.scopeItem,
                              selectedQuestionId === q.compositeId && styles.selectedItem
                            )}
                            onClick={(e) => onQuestionClick(e, q.compositeId)}
                          >
                            <div className={styles.scopeItemInfo}>
                              <StatusIcon status={questionStatuses[q.id]} />
                              <Highlighter
                                highlightClassName={styles.hlight}
                                searchWords={[inputValue]}
                                textToHighlight={q.data.questionName ?? ''}
                              />
                            </div>
                            <Button
                              type="icon"
                              icon={
                                <ArrowAscIcon
                                  style={{ width: 20, height: 20 }}
                                  className={styles.questionIcon}
                                />
                              }
                              className={styles.questionButton}
                              onClick={(e) => onGoToQuestionClick(e, q.route)}
                            />
                          </div>
                        ))}
                      </ul>
                    )}
                  />
                ))}
              {!filteredTree?.length && <div className={styles.noResults}>No questions found</div>}
            </div>
          </div>

          {/* Right panel with answers */}
          {selectedQuestionId !== null &&
            sharedAnswersCollection?.[selectedQuestionId] !== undefined && (
              <div className={styles.userAnswerBlock}>
                <div className={styles.usersAnswersList}>
                  {sharedAnswersCollection?.[selectedQuestionId].map((answer) => {
                    const ResolvedAnswer = answersResolver(answer.type);
                    return (
                      <div className={styles.userAnswerItem}>
                        <div className={styles.answerItemAction}>
                          <div className={styles.userData}>
                            <div className={styles.userInfo}>
                              <UserBlockSimple user={answer.user} />
                            </div>
                          </div>
                          <div className={styles.answerAction}>
                            {answer.state !== 'APPLIED' ? (
                              <Radio
                                value={answer.id}
                                checked={selectedAnswerIds[selectedQuestionId] === answer.id}
                                onClick={(e) =>
                                  setSelectedAnswerIds((value) => ({
                                    ...value,
                                    [selectedQuestionId]:
                                      value[selectedQuestionId] === e.target.value
                                        ? null
                                        : e.target.value,
                                  }))
                                }
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  padding: '8px 0',
                                }}
                              >
                                Select this answer
                              </Radio>
                            ) : (
                              <div className={styles.appliedAnswer}>Applied</div>
                            )}
                            <Dot3Menu onMenuClick={onMenuClick} MENU_LIST={MENU_LIST} />
                          </div>
                        </div>
                        <div className={styles.answercontent}>
                          <ResolvedAnswer answer={answer} />
                        </div>
                        {answer.files?.length > 0 && (
                          <HelpersFiles filesList={answer.files} questionCode={answer.id} />
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

          {/* Placeholder for no selection */}
          {selectedQuestionId == null && (
            <div className={styles.noResults}>
              <div>Select question to see answers</div>
            </div>
          )}

          {/* Placeholder for no answers */}
          {(selectedQuestionId !== null && sharedAnswersCollection?.[selectedQuestionId]) ===
            undefined && (
            <div className={styles.noResults}>
              <div>No answers found</div>
            </div>
          )}
        </div>

        {/* Footer section with action buttons */}
        <div className={styles.applyBlock}>
          <Button type="default" onClick={onBackClick}>
            Back
          </Button>
          <Button type="primary" disabled={!isAnswerChecked} onClick={onApplyAnswer}>
            Apply answer
          </Button>
        </div>
      </div>
    </>
  );
};

export default SharedAnswers;
